import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { Link, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { secureFetch } from "../data/secureFetch";

const {
  formatCentsToDollars,
  formatDateTimeToLocalString,
} = require("../store/calc-helpers");

const formatStatusBadge = (status = "") => {
  const normalized = `${status}`.toLowerCase();
  let colorScheme = "gray";
  if (normalized === "succeeded" || normalized === "completed")
    colorScheme = "green";
  else if (normalized === "active" || normalized === "pending")
    colorScheme = "blue";
  else if (normalized === "canceled" || normalized === "cancelled")
    colorScheme = "red";
  else if (normalized.includes("refund")) colorScheme = "orange";
  return <Badge colorScheme={colorScheme}>{status || "Unknown"}</Badge>;
};

function DonorHistory() {
  const { email } = useParams();
  const toast = useToast();
  const { organizationId } = useSelector((state) => state.user);
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  const decodedEmail = useMemo(() => {
    try {
      return decodeURIComponent(email || "");
    } catch {
      return email || "";
    }
  }, [email]);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    if (!organizationId || !decodedEmail) {
      setPayload({
        donor: { email: decodedEmail || "", first_name: "", last_name: "" },
        summary: {
          total_donations: 0,
          lifetime_succeeded_amount: 0,
          total_subscriptions: 0,
          active_subscriptions: 0,
          canceled_subscriptions: 0,
        },
        donations: [],
        subscriptions: [],
      });
      setLoading(false);
      return;
    }
    try {
      const response = await secureFetch(
        `/api/donations/${organizationId}/donors/${encodeURIComponent(
          decodedEmail,
        )}/history`,
      );
      if (!response.ok) {
        throw new Error(response.message || "Failed to load donor history");
      }
      setPayload(response);
    } catch (error) {
      toast({
        title: "Load failed",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setPayload({
        donor: { email: decodedEmail, first_name: "", last_name: "" },
        summary: {
          total_donations: 0,
          lifetime_succeeded_amount: 0,
          total_subscriptions: 0,
          active_subscriptions: 0,
          canceled_subscriptions: 0,
        },
        donations: [],
        subscriptions: [],
      });
    } finally {
      setLoading(false);
    }
  }, [decodedEmail, organizationId, toast]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" color="orange.400" />
      </Box>
    );
  }

  if (!payload) {
    return (
      <Box p={5}>
        <Text>No donor details available.</Text>
      </Box>
    );
  }

  const donorName = `${payload?.donor?.first_name || ""} ${
    payload?.donor?.last_name || ""
  }`.trim();
  const donations = Array.isArray(payload.donations) ? payload.donations : [];
  const subscriptions = Array.isArray(payload.subscriptions)
    ? payload.subscriptions
    : [];
  const summary = payload.summary || {};

  return (
    <Box p={5}>
      <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3}>
        <Box>
          <Heading size="lg">Donor History</Heading>
          <Text color="gray.600">
            {donorName || "Donor"}{" "}
            {payload?.donor?.email ? `(${payload.donor.email})` : ""}
          </Text>
        </Box>
        <Button as={Link} to="/donations" variant="outline">
          Back to Donations
        </Button>
      </HStack>

      <Wrap spacing={4} mb={6}>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="180px">
            <StatLabel>Total Donations</StatLabel>
            <StatNumber>{summary.total_donations || 0}</StatNumber>
          </Stat>
        </WrapItem>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="220px">
            <StatLabel>Lifetime Succeeded</StatLabel>
            <StatNumber>
              ${formatCentsToDollars(summary.lifetime_succeeded_amount || 0)}
            </StatNumber>
          </Stat>
        </WrapItem>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="180px">
            <StatLabel>Active Subscriptions</StatLabel>
            <StatNumber>{summary.active_subscriptions || 0}</StatNumber>
          </Stat>
        </WrapItem>
        <WrapItem>
          <Stat borderWidth="1px" borderRadius="md" p={4} minW="180px">
            <StatLabel>Canceled Subscriptions</StatLabel>
            <StatNumber>{summary.canceled_subscriptions || 0}</StatNumber>
          </Stat>
        </WrapItem>
      </Wrap>

      <Heading size="md" mb={3}>
        Donations
      </Heading>
      <Box borderWidth="1px" borderRadius="md" overflowX="auto" mb={8}>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Date</Th>
              <Th>Status</Th>
              <Th>Campaign/Fund</Th>
              <Th>Type</Th>
              <Th isNumeric>Donation</Th>
            </Tr>
          </Thead>
          <Tbody>
            {donations.length === 0 && (
              <Tr>
                <Td colSpan={6}>
                  <Text color="gray.500">
                    No donations found for this donor.
                  </Text>
                </Td>
              </Tr>
            )}
            {donations.map((row) => {
              const donation = Number(row.donation_amount || 0);
              return (
                <Tr key={`donation-${row.id}`}>
                  <Td>
                    <Link to={`/donations/${row.id}`}>{row.id}</Link>
                  </Td>
                  <Td>{formatDateTimeToLocalString(row.created_at)}</Td>
                  <Td>{formatStatusBadge(row.status)}</Td>
                  <Td>{row.campaign_name || row.fund_group_title || "N/A"}</Td>
                  <Td>{row.donation_type || "N/A"}</Td>
                  <Td isNumeric>${formatCentsToDollars(donation)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      <Heading size="md" mb={3}>
        Subscriptions
      </Heading>
      <Box borderWidth="1px" borderRadius="md" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Created</Th>
              <Th>Status</Th>
              <Th>Campaign</Th>
              <Th>Type</Th>
              <Th isNumeric>Amount</Th>
              <Th>Next Donation</Th>
              <Th>Payment Method</Th>
            </Tr>
          </Thead>
          <Tbody>
            {subscriptions.length === 0 && (
              <Tr>
                <Td colSpan={8}>
                  <Text color="gray.500">
                    No subscription history found for this donor.
                  </Text>
                </Td>
              </Tr>
            )}
            {subscriptions.map((row) => (
              <Tr key={`subscription-${row.id}`}>
                <Td>{row.id}</Td>
                <Td>{formatDateTimeToLocalString(row.created_at)}</Td>
                <Td>{formatStatusBadge(row.status)}</Td>
                <Td>{row.campaign_name || "N/A"}</Td>
                <Td>{row.donation_type || "N/A"}</Td>
                <Td isNumeric>
                  ${formatCentsToDollars(row.donation_amount || 0)}
                </Td>
                <Td>
                  {row.next_donation_date
                    ? formatDateTimeToLocalString(row.next_donation_date)
                    : "N/A"}
                </Td>
                <Td>{row.card_type || row.payment_method || "N/A"}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default DonorHistory;
